import React, { useContext } from 'react';
import { SweetAlertResult } from 'sweetalert2';
import useSuccessAlert from '../hooks/sweet-alert/use-success-alert';
import useWarningAlert from '../hooks/sweet-alert/use-warning-alert';
import useModificationAlert from '../hooks/sweet-alert/use-modification-alert';
import useConfirmAlert from '../hooks/sweet-alert/use-confirm-alert';
import useDeleteAlert from '../hooks/sweet-alert/use-delete-alert';
import useClassroomArchiveAlert from '../hooks/sweet-alert/use-classroom-archive-alert';
import { AlertVariant } from 'types/enums';
import { AlertOptions } from 'types';

export type UseAlert = [null, UseAlertDispatch];
export interface UseAlertDispatch {
  openAlert: (options?: AlertOptions) => void;
}

const throwErr = (message: string): Error => {
  throw new Error(message);
};

/**
 * UseAlert should control various states of sweet alert implementations
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AlertContext = React.createContext<any>(null);

const AlertProvider: React.FC = props => {
  const SuccessAlert = useSuccessAlert();
  const ConfirmAlert = useConfirmAlert();
  const WarningAlert = useWarningAlert();
  const ModificationAlert = useModificationAlert();
  const ClassroomArchiveAlert = useClassroomArchiveAlert();
  const DeleteAlert = useDeleteAlert();

  const AlertMap = {
    [AlertVariant.SUCCESS]: SuccessAlert,
    [AlertVariant.CONFIRM]: ConfirmAlert,
    [AlertVariant.WARNING]: WarningAlert,
    [AlertVariant.MODIFICATION]: ModificationAlert,
    [AlertVariant.CLASSROOM_ARCHIVE]: ClassroomArchiveAlert,
    [AlertVariant.DELETE]: DeleteAlert
  };

  const openAlert = React.useCallback(
    ({ variant, ...options }: AlertOptions): Promise<SweetAlertResult | void> => {
      const Alert = AlertMap[variant] || throwErr(`Alert variant (${variant}) does not exist!`);

      return Alert.open({ ...options });
    },
    [AlertMap]
  );

  return (
    <AlertContext.Provider
      value={[
        null,
        {
          openAlert
        }
      ]}
    >
      {props.children}
    </AlertContext.Provider>
  );
};

const useAlert = (): UseAlert => {
  const [state, dispatch] = useContext(AlertContext);

  return [state, dispatch];
};

export { AlertProvider, useAlert };
